import {
  getFirebaseClientApp,
  getFirebasePublicConfigStatus,
} from "./firebaseClient";
import {
  getRootServiceWorkerRegistration,
  ROOT_SERVICE_WORKER_SCOPE,
  ROOT_SERVICE_WORKER_URL,
} from "./serviceWorkerRegistration";
import {
  getFirebaseVapidKey,
  isPushRegistrationEnabled,
} from "./pushRegistrationConfig";
import { displayForegroundDirectMessageNotification } from "./foregroundDirectMessageNotifications";

let messagingClientPromise = null;
let messagingModulePromise = null;
export const PERMISSION_SETTLEMENT_WAIT_MS = 1000;
const PERMISSION_SETTLEMENT_POLL_MS = 25;

function defaultWindow() {
  return typeof window === "undefined" ? null : window;
}

function defaultNavigator() {
  return typeof navigator === "undefined" ? null : navigator;
}

function loadFirebaseMessagingModule() {
  return import("firebase/messaging");
}

function loadFirebaseInstallationsModule() {
  return import("firebase/installations");
}

function getMessagingModule(loader) {
  if (!messagingModulePromise) {
    let attempt;
    attempt = Promise.resolve()
      .then(loader)
      .catch((error) => {
        if (messagingModulePromise === attempt) messagingModulePromise = null;
        throw error;
      });
    messagingModulePromise = attempt;
  }
  return messagingModulePromise;
}

function unavailable(status, code) {
  return {
    status,
    code,
    messaging: null,
    serviceWorkerRegistration: null,
  };
}

function notificationPermission(windowObject) {
  try {
    return windowObject?.Notification?.permission || "unsupported";
  } catch {
    return "unsupported";
  }
}

async function waitForGrantedPermission(
  windowObject,
  { explicitGrant = false, waitMs = PERMISSION_SETTLEMENT_WAIT_MS } = {}
) {
  if (notificationPermission(windowObject) === "granted") return true;
  if (!explicitGrant) return false;
  const deadline = Date.now() + Math.max(0, waitMs);
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, PERMISSION_SETTLEMENT_POLL_MS));
    if (notificationPermission(windowObject) === "granted") return true;
  }
  return notificationPermission(windowObject) === "granted";
}

function urlPath(value, windowObject) {
  try {
    return value
      ? new URL(value, windowObject?.location?.href || "https://reelitpro.invalid/")
          .pathname
      : null;
  } catch {
    return null;
  }
}

async function probeFirebaseMessagingSupport(options) {
  const environment = options.environment ?? process.env;
  const configStatus = getFirebasePublicConfigStatus(environment);
  if (!configStatus.configured) {
    return unavailable("unconfigured", "firebase_client_unconfigured");
  }

  const windowObject = options.windowObject ?? defaultWindow();
  const navigatorObject = options.navigatorObject ?? defaultNavigator();
  if (!windowObject || !navigatorObject?.serviceWorker) {
    return unavailable("unsupported", "firebase_messaging_unsupported");
  }

  try {
    const loadMessaging =
      options.loadMessagingModule ?? loadFirebaseMessagingModule;
    const messagingModule = await getMessagingModule(loadMessaging);
    if (
      typeof messagingModule?.isSupported !== "function" ||
      typeof messagingModule?.getMessaging !== "function" ||
      !(await messagingModule.isSupported())
    ) {
      return unavailable("unsupported", "firebase_messaging_unsupported");
    }

    return { status: "supported", code: null, messagingModule };
  } catch {
    return unavailable("unavailable", "firebase_messaging_unavailable");
  }
}

async function initializeFirebaseMessagingClient(options) {
  const environment = options.environment ?? process.env;
  const windowObject = options.windowObject ?? defaultWindow();
  const navigatorObject = options.navigatorObject ?? defaultNavigator();
  const support = await probeFirebaseMessagingSupport(options);
  if (support.status !== "supported") return support;

  try {
    const firebaseApp = getFirebaseClientApp(environment);
    if (!firebaseApp) {
      return unavailable("unconfigured", "firebase_client_unconfigured");
    }

    const getWorkerRegistration =
      options.getWorkerRegistration ?? getRootServiceWorkerRegistration;
    const workerResult = await getWorkerRegistration({
      windowObject,
      navigatorObject,
    });
    if (workerResult?.status !== "ready" || !workerResult.registration) {
      return unavailable("unavailable", "service_worker_unavailable");
    }

    return {
      status: "ready",
      code: null,
      messaging: support.messagingModule.getMessaging(firebaseApp),
      serviceWorkerRegistration: workerResult.registration,
    };
  } catch {
    return unavailable("unavailable", "firebase_messaging_unavailable");
  }
}

export function getFirebaseMessagingClient(options = {}) {
  if (!messagingClientPromise) {
    let attempt;
    attempt = initializeFirebaseMessagingClient(options).then((client) => {
      if (messagingClientPromise === attempt && client.status !== "ready") {
        messagingClientPromise = null;
      }
      return client;
    });
    messagingClientPromise = attempt;
  }
  return messagingClientPromise;
}

export async function isFirebaseMessagingSupported(options = {}) {
  const result = await probeFirebaseMessagingSupport(options);
  return result.status === "supported";
}

export async function createFirebaseRegistrationSession(options = {}) {
  const environment = options.environment ?? process.env;
  const sessionKey =
    typeof options.sessionKey === "string" ? options.sessionKey.trim() : "";
  if (!isPushRegistrationEnabled(environment)) {
    return unavailable("disabled", "push_registration_feature_disabled");
  }
  if (!sessionKey) {
    return unavailable("unauthenticated", "push_registration_login_required");
  }
  const vapidKey = getFirebaseVapidKey(environment);

  const client = await getFirebaseMessagingClient(options);
  if (client.status !== "ready" || !client.messaging) return client;

  const loadMessaging = options.loadMessagingModule ?? loadFirebaseMessagingModule;
  let messagingModule;
  try {
    messagingModule = await getMessagingModule(loadMessaging);
  } catch {
    return unavailable("unavailable", "firebase_messaging_unavailable");
  }
  if (
    typeof messagingModule?.register !== "function" ||
    typeof messagingModule?.unregister !== "function" ||
    typeof messagingModule?.onRegistered !== "function" ||
    typeof messagingModule?.onUnregistered !== "function"
  ) {
    return unavailable("unavailable", "firebase_messaging_unavailable");
  }

  let disposed = false;
  let registerPromise = null;
  let unregisterPromise = null;
  const registeredUnsubscribe = messagingModule.onRegistered(
    client.messaging,
    (fid) => {
      if (!disposed) options.onRegistered?.(fid);
    }
  );
  const unregisteredUnsubscribe = messagingModule.onUnregistered(
    client.messaging,
    () => {
      if (!disposed) options.onUnregistered?.();
    }
  );
  const foregroundMessageUnsubscribe =
    typeof messagingModule.onMessage === "function"
      ? messagingModule.onMessage(client.messaging, (payload) => {
          displayForegroundDirectMessageNotification(
            client.serviceWorkerRegistration,
            payload,
            { locationObject: options.windowObject?.location ?? defaultWindow()?.location }
          ).catch(() => {});
        })
      : null;

  return {
    status: "ready",
    code: null,
    async register(registrationOptions = {}) {
      if (!vapidKey) {
        throw new Error("push_registration_unavailable");
      }
      const permissionWindow = options.windowObject ?? defaultWindow();
      if (
        disposed ||
        !(await waitForGrantedPermission(permissionWindow, {
          explicitGrant: registrationOptions.permissionGranted === true,
          waitMs: options.permissionSettlementWaitMs,
        }))
      ) {
        throw new Error("push_registration_permission_required");
      }
      if (!registerPromise) {
        registerPromise = Promise.resolve(
          messagingModule.register(client.messaging, {
            vapidKey,
            serviceWorkerRegistration: client.serviceWorkerRegistration,
          })
        ).finally(() => {
          registerPromise = null;
        });
      }
      return registerPromise;
    },
    unregister() {
      if (disposed) {
        return Promise.reject(new Error("push_registration_session_disposed"));
      }
      if (!unregisterPromise) {
        unregisterPromise = Promise.resolve(
          messagingModule.unregister(client.messaging)
        ).finally(() => {
          unregisterPromise = null;
        });
      }
      return unregisterPromise;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      registeredUnsubscribe?.();
      unregisteredUnsubscribe?.();
      foregroundMessageUnsubscribe?.();
    },
  };
}

export async function inspectFirebaseRegistrationHealth(options = {}) {
  const windowObject = options.windowObject ?? defaultWindow();
  const navigatorObject = options.navigatorObject ?? defaultNavigator();
  const permission = notificationPermission(windowObject);
  const snapshot = {
    status: "unhealthy",
    code: null,
    notificationPermission: permission,
    rootWorkerPresent: false,
    workerActive: false,
    workerController: false,
    workerScriptPath: null,
    rootScope: false,
    workerScopePath: null,
    pushSubscriptionExists: false,
    firebaseMessagingInitialized: false,
  };
  if (permission !== "granted") {
    return { ...snapshot, code: "push_registration_permission_required" };
  }

  const getWorkerRegistration =
    options.getWorkerRegistration ?? getRootServiceWorkerRegistration;
  let workerResult;
  try {
    workerResult = await getWorkerRegistration({
      windowObject,
      navigatorObject,
    });
  } catch {
    return { ...snapshot, code: "service_worker_unavailable" };
  }
  const registration = workerResult?.registration;
  const scopePath = urlPath(registration?.scope, windowObject);
  const scriptPath = urlPath(registration?.active?.scriptURL, windowObject);
  const controllerPath = urlPath(
    navigatorObject?.serviceWorker?.controller?.scriptURL,
    windowObject
  );
  snapshot.rootWorkerPresent = workerResult?.status === "ready" && Boolean(registration);
  snapshot.workerActive = Boolean(registration?.active);
  snapshot.workerController = controllerPath === ROOT_SERVICE_WORKER_URL;
  snapshot.workerScriptPath = scriptPath;
  snapshot.workerScopePath = scopePath;
  snapshot.rootScope = scopePath === ROOT_SERVICE_WORKER_SCOPE;
  if (
    !snapshot.rootWorkerPresent ||
    !snapshot.workerActive ||
    !snapshot.rootScope ||
    scriptPath !== ROOT_SERVICE_WORKER_URL
  ) {
    return { ...snapshot, code: "service_worker_unavailable" };
  }

  try {
    snapshot.pushSubscriptionExists = Boolean(
      await registration.pushManager?.getSubscription?.()
    );
  } catch {
    return { ...snapshot, code: "push_subscription_unavailable" };
  }
  if (!snapshot.pushSubscriptionExists) {
    return { ...snapshot, code: "push_subscription_missing" };
  }

  const client = await getFirebaseMessagingClient({
    ...options,
    windowObject,
    navigatorObject,
    getWorkerRegistration: async () => workerResult,
  });
  snapshot.firebaseMessagingInitialized =
    client?.status === "ready" && Boolean(client.messaging);
  if (!snapshot.firebaseMessagingInitialized) {
    return { ...snapshot, code: "firebase_messaging_unavailable" };
  }
  return { ...snapshot, status: "healthy", code: null };
}

export async function resetFirebaseRegistrationForUnregisteredTarget(
  options = {}
) {
  const environment = options.environment ?? process.env;
  const firebaseApp = getFirebaseClientApp(environment);
  if (!firebaseApp) {
    return unavailable("unconfigured", "firebase_client_unconfigured");
  }

  try {
    const getWorkerRegistration =
      options.getWorkerRegistration ?? getRootServiceWorkerRegistration;
    const workerResult = await getWorkerRegistration({
      windowObject: options.windowObject ?? defaultWindow(),
      navigatorObject: options.navigatorObject ?? defaultNavigator(),
    });
    const pushManager = workerResult?.registration?.pushManager;
    if (
      workerResult?.status !== "ready" ||
      typeof pushManager?.getSubscription !== "function"
    ) {
      return unavailable("unavailable", "service_worker_unavailable");
    }
    const existingSubscription = await pushManager.getSubscription();
    if (existingSubscription) {
      if (
        typeof existingSubscription.unsubscribe !== "function" ||
        !(await existingSubscription.unsubscribe())
      ) {
        return unavailable(
          "unavailable",
          "firebase_registration_reset_unavailable"
        );
      }
    }

    const loadInstallations =
      options.loadInstallationsModule ?? loadFirebaseInstallationsModule;
    const installationsModule = await loadInstallations();
    if (
      typeof installationsModule?.getInstallations !== "function" ||
      typeof installationsModule?.deleteInstallations !== "function"
    ) {
      return unavailable("unavailable", "firebase_registration_reset_unavailable");
    }
    await installationsModule.deleteInstallations(
      installationsModule.getInstallations(firebaseApp)
    );
    return { status: "reset", code: null };
  } catch {
    return unavailable("unavailable", "firebase_registration_reset_unavailable");
  }
}

export async function cleanupFirebaseRegistration(options = {}) {
  const client = await getFirebaseMessagingClient(options);
  if (client.status !== "ready" || !client.messaging) return client;

  const loadMessaging = options.loadMessagingModule ?? loadFirebaseMessagingModule;
  let messagingModule;
  try {
    messagingModule = await getMessagingModule(loadMessaging);
  } catch {
    return unavailable("unavailable", "firebase_messaging_unavailable");
  }
  if (typeof messagingModule?.unregister !== "function") {
    return unavailable("unavailable", "firebase_messaging_unavailable");
  }

  try {
    await messagingModule.unregister(client.messaging);
    return { status: "unregistered", code: null };
  } catch {
    return unavailable("unavailable", "firebase_messaging_unavailable");
  }
}

export function resetFirebaseMessagingClientForTests() {
  invalidateFirebaseMessagingInitialization();
}

// Drops only cached initialization promises. It does not unregister FCM,
// delete an installation, or alter an existing PushSubscription.
export function invalidateFirebaseMessagingInitialization() {
  messagingClientPromise = null;
  messagingModulePromise = null;
}
